import React, { useState } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../services/api';

/**
 * Shown instead of the app until a new password is set (15 Sep 2026).
 *
 * The database was readable with the public key while it was open, password
 * hashes included, so every account sets a new password at its next sign-in.
 * The server enforces it too: until this succeeds, every other call answers
 * "password change required".
 */
const ChangePasswordScreen = () => {
  const { user, completePasswordChange, logout } = useAuth();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const rules = [
    { ok: form.next.length >= 10, label: 'At least 10 characters' },
    { ok: /[A-Za-z]/.test(form.next) && /[0-9]/.test(form.next), label: 'A letter and a number' },
    { ok: !!form.next && form.next !== form.current, label: 'Different from your current password' },
    { ok: !!form.next && form.next === form.confirm, label: 'Both new passwords match' }
  ];

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!rules.every(r => r.ok)) { setError('Your new password does not meet the rules below yet.'); return; }
    setSaving(true);
    try {
      const result = await dashboardAPI.changePassword(form.current, form.next);
      if (result && result.success) {
        completePasswordChange(result);
      } else {
        setError((result && result.error) || 'Could not change the password.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not change the password.');
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, autoComplete) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`pw-${key}`}>{label}</label>
      <div className="relative">
        <input
          id={`pw-${key}`}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
          required
        />
        {key === 'current' && (
          <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-2 text-gray-400 hover:text-gray-600" aria-label={show ? 'Hide passwords' : 'Show passwords'}>
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl shadow-md w-full max-w-md p-8">
        <div className="flex items-center gap-2 mb-2">
          <Lock size={22} className="text-[#679632]" />
          <h1 className="text-xl font-bold text-gray-900">Set a new password</h1>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Hi {user?.name || 'there'} — for security, everyone on the team is choosing a new password.
          It takes a moment, and you only do it once.
        </p>
        <form onSubmit={submit} className="space-y-4">
          {field('current', 'Current password', 'current-password')}
          {field('next', 'New password', 'new-password')}
          {field('confirm', 'Confirm new password', 'new-password')}
          <ul className="text-xs space-y-1" aria-label="Password rules">
            {rules.map(r => (
              <li key={r.label} className={r.ok ? 'text-[#587f2b]' : 'text-gray-500'}>{r.ok ? '✓' : '•'} {r.label}</li>
            ))}
          </ul>
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <button type="submit" disabled={saving} className="w-full px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50">
            {saving ? 'Saving…' : 'Save new password'}
          </button>
          <button type="button" onClick={logout} className="w-full text-sm text-gray-500 hover:text-gray-700">Sign out</button>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordScreen;
